// AgentPanel — the ENG-1029 editor agent: a docked chat column on the right
// rail where the user asks an LLM (running server-side on the aos-mcp endpoint,
// wired to the MCP tool registry) to edit blueprints and assets.
//
// Same chrome contract as InspectorPanel (DockedShell.jsx): collapses to a
// 34px strip, ⌥G toggles, width lives in shellLayout (agentW/agentOpen). Chat
// state lives in this component — the panel stays mounted across collapse, so
// a conversation survives it.
//
// The blueprint-refresh hook: nothing in the app watches for server-side
// blueprint changes, so when a turn lands a successful save_blueprint the panel
// is the one place that KNOWS the server moved. agentRefreshRules decides:
// editor closed → auto-reload (onAutoReload → loadBlueprintFromDB), editor
// open → an explicit Reload affordance (onPromptReload → the editor's own
// Load-from-Cloud path) so unsaved local work is never silently clobbered.

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { MONO, UI_FONT } from '../PipelineGlyph.jsx'
import { shellLayout, COLLAPSED_SIZE } from '../../lib/shellLayout.js'
import { streamAgentChat } from '../../lib/agentApi.js'
import { decideBlueprintRefresh, isBlueprintSaveEvent, isMaterialToolEvent } from '../../lib/agentRefreshRules.js'
import { isClientActionEvent, runClientAction } from '../../lib/agentClientActions.js'
import { requestFocus } from '../../lib/graphFocus.js'
import { describeLocation } from '../../lib/graphNodeSearch.js'
import { head } from '../../lib/studioStyles.js'

const LINE = '1px solid var(--m-line)'
const label = { ...head, whiteSpace: 'nowrap' }
const mutedMono = { fontFamily: MONO, fontSize: 9, color: 'var(--m-fg-3)', whiteSpace: 'nowrap' }

function useStore(store) {
  const [state, setState] = useState(store.getState())
  useEffect(() => store.subscribe(() => setState({ ...store.getState() })), [store])
  return state
}

let feedSeq = 0
const feedItem = (kind, props) => ({ id: ++feedSeq, kind, ...props })

export function AgentPanel({ character, editorOpen, onAutoReload, onPromptReload }) {
  const layout = useStore(shellLayout)
  const [feed, setFeed] = useState([]) // display items: user/assistant/tool/warning/error/info/reload-prompt
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const historyRef = useRef([]) // LLM chat history: [{role, content}]
  const abortRef = useRef(null)
  const scrollRef = useRef(null)
  // Live values for the window seam + async handlers without re-binding.
  const liveRef = useRef({})
  liveRef.current = { character, editorOpen, onAutoReload, onPromptReload, busy }

  const append = useCallback((item) => setFeed((f) => [...f, item]), [])
  const patchLast = useCallback((kind, fn) => setFeed((f) => {
    const i = f.findLastIndex((it) => it.kind === kind)
    if (i < 0) return f
    const next = f.slice()
    next[i] = fn(next[i])
    return next
  }), [])

  // ⌥G toggles, mirroring the inspector's ⌥I.
  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey && e.code === 'KeyG') {
        e.preventDefault()
        shellLayout.toggleAgent()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // Follow the newest message.
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [feed])

  // A material row changed server-side: rebind through the same live-preview
  // hook the Inspector's PATCHes use. No prompt needed — material edits have no
  // unsaved-local-draft hazard the way blueprint graphs do.
  const handleMaterialEvent = useCallback(() => {
    const reload = typeof window !== 'undefined' ? window.__RELOAD_MATERIALS__ : null
    if (typeof reload === 'function') {
      Promise.resolve(reload()).then(
        () => append(feedItem('info', { text: 'Material updated — viewport rebound live.' })),
        (err) => append(feedItem('error', { text: `Material saved on the server, but rebinding the viewport failed: ${err?.message ?? err}` })),
      )
    } else {
      append(feedItem('info', { text: 'Material updated on the server. Reload to see it (no live material hook on this view).' }))
    }
  }, [append])

  // The server asked the app to do something (today: focus_node navigating the
  // graph editor). The action runs AND leaves a chip in the transcript, so the
  // same node can be jumped to again later without re-asking the agent.
  const handleClientAction = useCallback((ev) => {
    if (ev.action === 'focus-node') append(feedItem('focus', { payload: ev.payload }))
    Promise.resolve(runClientAction(ev)).then(
      (msg) => { if (msg) append(feedItem('info', { text: msg })) },
      (err) => append(feedItem('error', { text: err?.message ?? String(err) })),
    )
  }, [append])

  const handleSaveEvent = useCallback(() => {
    const { editorOpen: open, onAutoReload: auto, onPromptReload: prompt } = liveRef.current
    if (decideBlueprintRefresh({ editorOpen: open }) === 'auto-reload') {
      Promise.resolve(auto?.()).then(
        () => append(feedItem('info', { text: 'Blueprint saved — editor state reloaded from the server.' })),
        (err) => append(feedItem('error', { text: `Blueprint saved on the server, but reloading the editor failed: ${err?.message ?? err}` })),
      )
    } else {
      append(feedItem('reload-prompt', { onReload: prompt }))
    }
  }, [append])

  const send = useCallback(async (text) => {
    const trimmed = String(text ?? '').trim()
    if (!trimmed || liveRef.current.busy) return
    const { character: slug, editorOpen: open } = liveRef.current
    const messages = [...historyRef.current, { role: 'user', content: trimmed }]
    historyRef.current = messages
    setDraft('')
    setBusy(true)
    append(feedItem('user', { text: trimmed }))
    append(feedItem('assistant', { text: '', streaming: true }))

    let assistantText = ''
    const abort = new AbortController()
    abortRef.current = abort
    try {
      await streamAgentChat({
        messages,
        context: { character: slug, graph: open || undefined },
        signal: abort.signal,
        onEvent: (ev) => {
          if (ev.type === 'text-delta') {
            assistantText += ev.text
            patchLast('assistant', (it) => ({ ...it, text: assistantText }))
          } else if (ev.type === 'tool-start') {
            append(feedItem('tool', { name: ev.name, status: 'running' }))
          } else if (ev.type === 'tool-result') {
            patchLast('tool', (it) => (it.name === ev.name && it.status === 'running')
              ? { ...it, status: ev.ok ? 'ok' : 'error', summary: ev.summary }
              : it)
            if (isBlueprintSaveEvent(ev)) handleSaveEvent()
            if (isMaterialToolEvent(ev)) handleMaterialEvent()
          } else if (isClientActionEvent(ev)) {
            handleClientAction(ev)
          } else if (ev.type === 'warning') {
            append(feedItem('warning', { text: ev.message }))
          } else if (ev.type === 'error') {
            append(feedItem('error', { text: ev.message }))
          }
        },
      })
    } catch (err) {
      if (abort.signal.aborted) {
        append(feedItem('info', { text: 'Stopped.' }))
      } else {
        append(feedItem('error', { text: err?.message ?? String(err) }))
      }
    } finally {
      abortRef.current = null
      setBusy(false)
      patchLast('assistant', (it) => ({ ...it, streaming: false }))
      if (assistantText) historyRef.current = [...historyRef.current, { role: 'assistant', content: assistantText }]
    }
  }, [append, patchLast, handleSaveEvent, handleMaterialEvent, handleClientAction])

  const stop = useCallback(() => { abortRef.current?.abort() }, [])

  // Test/console seam, following the window.__* convention (CLAUDE.md).
  useEffect(() => {
    window.__AGENT_PANEL__ = {
      open: () => shellLayout.setState({ agentOpen: true }),
      close: () => shellLayout.setState({ agentOpen: false }),
      send,
      stop,
      get state() {
        return { open: shellLayout.getState().agentOpen, busy: liveRef.current.busy, feedLength: feedSeq }
      },
    }
    return () => { delete window.__AGENT_PANEL__ }
  }, [send, stop])

  if (!layout.agentOpen) {
    return (
      <div
        data-testid="shell-agent"
        data-collapsed="1"
        onClick={() => shellLayout.toggleAgent()}
        title="Expand agent (⌥G)"
        style={{
          flex: 'none', width: COLLAPSED_SIZE, background: 'var(--m-panel)',
          borderLeft: LINE, cursor: 'pointer', userSelect: 'none',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 10, padding: '8px 0',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'color-mix(in srgb, var(--m-fg) 4%, var(--m-panel))' }}
        onMouseLeave={e => { e.currentTarget.style.background = 'var(--m-panel)' }}
      >
        <span style={{ color: 'var(--m-fg)', fontSize: 10 }}>◂</span>
        <span style={{ ...label, writingMode: 'vertical-rl', color: 'var(--m-fg)' }}>AGENT</span>
        <span style={{ flex: 1 }} />
        <span style={mutedMono}>⌥G</span>
      </div>
    )
  }

  return (
    <div
      data-testid="shell-agent"
      data-collapsed="0"
      style={{
        flex: 'none', width: layout.agentW, background: 'var(--m-panel)',
        borderLeft: LINE, display: 'flex', flexDirection: 'column', minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderBottom: LINE, userSelect: 'none' }}>
        <span onClick={() => shellLayout.toggleAgent()} title="Collapse agent (⌥G)" style={{ ...label, cursor: 'pointer' }}>AGENT ▸</span>
        <span style={{ flex: 1 }} />
        <span style={mutedMono}>{character ?? 'no character'}</span>
      </div>

      <div ref={scrollRef} className="aos-thin-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 10, display: 'grid', gap: 6, alignContent: 'start' }}>
        {feed.length === 0 && (
          <div style={{ ...mutedMono, whiteSpace: 'normal', lineHeight: 1.6 }}>
            Ask the agent to inspect or edit this character&apos;s blueprints — e.g.
            &quot;add a print node to the event graph that logs every gesture&quot;.
            It works through the same server tools the MCP endpoint exposes and
            saves through the normal blueprint history.
          </div>
        )}
        {feed.map((item) => <FeedItem key={item.id} item={item} />)}
      </div>

      <div style={{ borderTop: LINE, padding: 8, display: 'grid', gap: 6 }}>
        <textarea
          data-testid="agent-input"
          value={draft}
          disabled={busy}
          placeholder={busy ? 'working…' : 'Ask the agent…'}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation() // typing must not drive viewport/gizmo shortcuts
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(draft) }
          }}
          rows={2}
          style={{
            resize: 'none', background: 'var(--m-bg)', border: LINE,
            borderRadius: 'var(--m-radius-sm)', padding: '6px 8px',
            fontFamily: UI_FONT, fontSize: 11, color: 'var(--m-fg)', outline: 'none',
          }}
        />
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            data-testid="agent-send"
            disabled={busy || !draft.trim()}
            onClick={() => send(draft)}
            style={{
              flex: 1, padding: '5px 0', fontSize: 10.5, fontFamily: UI_FONT,
              borderRadius: 999, cursor: busy || !draft.trim() ? 'default' : 'pointer',
              border: `1px solid ${busy || !draft.trim() ? 'var(--m-line-hi)' : 'var(--m-accent)'}`,
              background: busy || !draft.trim() ? 'var(--m-panel-hi)' : 'var(--m-accent-soft)',
              color: busy || !draft.trim() ? 'var(--m-fg-3)' : 'var(--m-accent)',
            }}
          >{busy ? 'working…' : 'Send'}</button>
          {busy && (
            <button
              type="button"
              data-testid="agent-stop"
              onClick={stop}
              style={{
                flex: 'none', padding: '5px 14px', fontSize: 10.5, fontFamily: UI_FONT,
                borderRadius: 999, cursor: 'pointer',
                border: '1px solid var(--m-danger)', background: 'transparent', color: 'var(--m-danger)',
              }}
            >Stop</button>
          )}
        </div>
      </div>
    </div>
  )
}

function FeedItem({ item }) {
  if (item.kind === 'user' || item.kind === 'assistant') {
    const user = item.kind === 'user'
    return (
      <div
        data-testid={`agent-msg-${item.kind}`}
        style={{
          justifySelf: user ? 'end' : 'start', maxWidth: '92%',
          padding: '6px 9px', borderRadius: 8, whiteSpace: 'pre-wrap',
          fontFamily: UI_FONT, fontSize: 11, lineHeight: 1.5,
          background: user ? 'var(--m-accent-soft)' : 'var(--m-bg)',
          border: `1px solid ${user ? 'var(--m-accent)' : 'var(--m-line)'}`,
          color: 'var(--m-fg)',
        }}
      >
        {item.text || (item.streaming ? '…' : '')}
      </div>
    )
  }
  if (item.kind === 'tool') {
    const tone = item.status === 'error' ? 'var(--m-danger)' : item.status === 'ok' ? 'var(--m-accent)' : 'var(--m-fg-2)'
    return (
      <div
        data-testid="agent-tool-chip"
        data-tool={item.name}
        data-status={item.status}
        title={item.summary ?? ''}
        style={{
          justifySelf: 'start', display: 'flex', alignItems: 'center', gap: 6,
          padding: '3px 8px', borderRadius: 999, border: `1px solid ${tone}`,
          fontFamily: MONO, fontSize: 9, color: tone, maxWidth: '92%',
        }}
      >
        <span>{item.status === 'running' ? '⏳' : item.status === 'ok' ? '✓' : '✕'}</span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
      </div>
    )
  }
  if (item.kind === 'focus') {
    // Stays in the transcript: the jump already happened, but "take me back
    // there" is the next thing you want and re-asking the agent for it is a
    // round trip through an LLM to reach a node you were just looking at.
    const p = item.payload ?? {}
    const where = describeLocation({ graph: p.graph === 'events' ? 'event' : p.graph, container: p.container })
    return (
      <button
        type="button"
        data-testid="agent-focus-chip"
        data-node-id={p.nodeId}
        onClick={() => requestFocus(p)}
        title={`Go to ${p.nodeId} — ${where}`}
        style={{
          justifySelf: 'start', display: 'flex', alignItems: 'center', gap: 6, maxWidth: '92%',
          padding: '3px 8px', borderRadius: 999, cursor: 'pointer',
          border: '1px solid var(--m-accent)', background: 'var(--m-accent-soft)',
          fontFamily: MONO, fontSize: 9, color: 'var(--m-accent)',
        }}
      >
        <span>⤢</span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.label ?? p.nodeId}
        </span>
        <span style={{ color: 'var(--m-fg-3)' }}>{where}</span>
      </button>
    )
  }
  if (item.kind === 'reload-prompt') {
    return (
      <div
        data-testid="agent-reload-prompt"
        style={{
          padding: '7px 9px', borderRadius: 'var(--m-radius-sm)',
          border: '1px solid var(--m-warn)', background: 'color-mix(in srgb, var(--m-warn) 8%, transparent)',
          fontFamily: UI_FONT, fontSize: 10.5, color: 'var(--m-fg)', display: 'grid', gap: 6,
        }}
      >
        <span style={{ lineHeight: 1.5 }}>
          The agent saved this blueprint on the server. The open editor may hold
          unsaved local edits, so it was not reloaded automatically.
        </span>
        <button
          type="button"
          data-testid="agent-reload-editor"
          onClick={() => item.onReload?.()}
          style={{
            justifySelf: 'start', padding: '3px 12px', fontSize: 10, fontFamily: UI_FONT,
            borderRadius: 999, cursor: 'pointer',
            border: '1px solid var(--m-warn)', background: 'transparent', color: 'var(--m-warn)',
          }}
        >Load from Cloud…</button>
      </div>
    )
  }
  const tone = item.kind === 'error' ? 'var(--m-danger)' : item.kind === 'warning' ? 'var(--m-warn)' : 'var(--m-fg-3)'
  return (
    <div
      data-testid={`agent-${item.kind}`}
      style={{ fontFamily: MONO, fontSize: 9.5, color: tone, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}
    >
      {item.text}
    </div>
  )
}

export default AgentPanel
